import * as React from 'react';
import {CRASH_SEVERITY, crashHour} from './crashHeatLayers';

/**
 * What a probe, work zone, speed-change or reliability feature says when you
 * click it. Shared, because these layers are drawn on two maps now -- the
 * segment map in the App Center and the live camera map -- and a work zone
 * should not describe itself differently depending on which one you opened.
 */
export interface ProbeFeature {
    layer: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    p: any;
}

const num = (v: unknown, digits = 0): string =>
    v === null || v === undefined || v === '' || Number.isNaN(Number(v))
        ? 'n/a'
        : Number(v).toFixed(digits);

const ProbePopup: React.FC<ProbeFeature> = ({layer, p}: ProbeFeature) => (
    <div className="probe-popup">
        {layer === 'probe-speeds' && (
            <>
                <p className="probe-popup__title">{p.roadwayname || p.tmc || 'Probe segment'}</p>
                {Number(p.ss_jam_factor) >= 10 ? (
                    <p className="probe-popup__closed">Closed (jam factor 10)</p>
                ) : (
                    <>
                        <p>Speed: {num(p.ss_speed)} mph
                            {p.ss_free_flow != null && ` (free flow ${num(p.ss_free_flow)})`}</p>
                        <p>Jam factor: {num(p.ss_jam_factor, 1)} / 10</p>
                    </>
                )}
            </>
        )}

        {layer === 'anomaly-line' && (
            <>
                <p className="probe-popup__title">{p.roadway || p.seg_key || 'Segment'}</p>
                <p>
                    {num(Math.abs(Number(p.sec_per_mi)))} sec/mi
                    {String(p.slower) === 'true' ? ' slower' : ' faster'} than usual
                </p>
                {p.grain && <p className="probe-popup__meta">grain: {p.grain}</p>}
            </>
        )}

        {layer === 'workzone-line' && (
            <>
                <p className="probe-popup__title">
                    {p.county ? `${p.county} County` : 'Work zone'}
                    {p.district && ` - district ${p.district}`}
                </p>
                {p.delay != null && <p>Delay: {num(p.delay, 1)} min</p>}
                {p.speed_limit != null &&
                    <p>Work zone limit: {num(p.speed_limit)} mph
                        {p.posted_limit_mph != null && ` (posted ${num(p.posted_limit_mph)})`}</p>}
                {p.vehicle_impact && <p>{String(p.vehicle_impact).replace(/-/g, ' ')}</p>}
                {p.end_date && <p className="probe-popup__meta">ends {String(p.end_date).slice(0, 10)}</p>}
            </>
        )}

        {layer === 'crash-live' && (
            <>
                <p className="probe-popup__title">
                    {p.on_street_name || 'Crash'}
                    {p.at_cross_street && ` ${p.at_cross_street}`}
                </p>
                {p.event_description &&
                    <p className="probe-popup__desc">{String(p.event_description).trim()}</p>}
                {p.lanes_blocked != null && Number(p.lanes_blocked) > 0 &&
                    <p>{p.lanes_blocked} lane{Number(p.lanes_blocked) === 1 ? '' : 's'} blocked</p>}
                <p className="probe-popup__meta">
                    {p.county}{p.pub_millis ? ` \u00b7 reported ${String(p.pub_millis).slice(0, 16)}` : ''}
                </p>
            </>
        )}

        {layer === 'crash-heat-points' && (
            <>
                <p className="probe-popup__title">{p.loc || 'Crash'}</p>
                {p.sev !== undefined &&
                    <p>{CRASH_SEVERITY[Number(p.sev)] ?? 'Unknown severity'}</p>}
                <p className="probe-popup__meta">
                    {p.dt}{crashHour(p.hr) ? ` \u00b7 ${crashHour(p.hr)}` : ''}
                </p>
                {p.county && <p className="probe-popup__meta">{p.county}</p>}
            </>
        )}

        {layer === 'reliability-line' && (
            <>
                <p className="probe-popup__title">{p.roadway || p.tmc || p.seg_key || 'Segment'}</p>
                <p>Planning time index: {num(p.pti, 2)}</p>
                {p.bi != null && <p>Buffer index: {num(p.bi, 2)}</p>}
                {p.grain && <p className="probe-popup__meta">grain: {p.grain}</p>}
            </>
        )}
    </div>
);

export default ProbePopup;
